/**
 * Typed error model. `ApiError` is thrown for every non-2xx response and for
 * network failures. Callers switch on `code` (e.g. the Members screen renders a
 * dedicated "not authorized" state on `FORBIDDEN`).
 *
 * Deliberately small: dink-admin's genesis surface is read-only against
 * dink-api plus the saas-auth refresh/logout/csrf calls, so it does not need
 * saas-admin's large admin-mutation error union.
 */
export type ErrorCode =
  | 'NETWORK_ERROR'
  | 'SESSION_EXPIRED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'UNKNOWN';

export interface ApiErrorInit {
  requestId?: string;
  retryable?: boolean;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string | undefined;
  readonly retryable: boolean | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(code: ErrorCode, status: number, init: ApiErrorInit = {}) {
    super(`${code} (${status})`);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.requestId = init.requestId;
    this.retryable = init.retryable;
    this.details = init.details;
  }
}

/** Map an HTTP status to an `ErrorCode` for responses with no usable body. */
export function statusToCode(status: number): ErrorCode {
  if (status === 400) return 'VALIDATION';
  if (status === 401) return 'SESSION_EXPIRED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 429) return 'RATE_LIMITED';
  return 'UNKNOWN';
}
